import {
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Typography,
    Grid,
    Paper,
    Theme,
    Container,
    CircularProgress,
} from "@mui/material";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "util";
import ICircuit from "../../models/ICircuit";
import CircuitServiceInstance from "../../services/CircuitService";
import TrackdaysCalendarControl from "../common/TrackdaysCalendarControl";
import config from "../../config";
import axios from "axios";
import OntimeBreadcrumb from "../common/OntimeBreadcrumb";
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import EventIcon from '@mui/icons-material/Event';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        media: {
            height: 140,
        },
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        appBarSpacer: theme.mixins.toolbar,
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        navButton: {
            marginRight: theme.spacing(1),
        },
    }));








interface ICircuitCardProps {
    circuit?: ICircuit;
}

function CircuitCard(props: ICircuitCardProps) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const { circuit } = props;
    if (circuit) {
        return (
            <Card className={classes.root}>
                <CardActionArea>
                    <CardMedia
                        className={classes.media}
                        image={circuit.logoUrl}
                        title={circuit.name} />
                    <CardContent>
                        <Typography gutterBottom variant="h5" component="h2">
                            {circuit.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="p">
                            {format(t("circuit.location"), circuit.location)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="p">
                            {format(t("circuit.length"), circuit.length)}
                        </Typography>
                    </CardContent>
                </CardActionArea>

            </Card>);
    }
    else {
        return (<></>);
    }
}
// interface ICircuitMatchParams {
//     circuitId: string;
// }

// interface ICircuitDetailState {
//     circuitId: string;
//     circuit?: ICircuit;
// }

function CircuitDetail(props: ICircuitCardProps) {
    const { circuitId } = useParams();
    const [circuit, setCircuit] = useState<ICircuit | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const classes = useStyles();
    const { t } = useTranslation("common");
    const navigate = useNavigate();

    useEffect(() => {
        if (!circuitId) {
            return;
        };

        setIsLoading(true);
        setCircuit(undefined);

        const source = axios.CancelToken.source();
        CircuitServiceInstance.getCircuit(circuitId, source.token)
            .then((fetchedCircuit: ICircuit) => {
                setCircuit(fetchedCircuit);
                setIsLoading(false);
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    console.error("Failed to fetch circuit:", error);
                    setIsLoading(false);
                }
            });

        return () => {
            source.cancel();
        };
    }, [circuitId]);


    // Use isLoading state here for conditional rendering (e.g., show spinner)
    if (isLoading) {
        return <CircularProgress />;
    }

    if (!circuitId) {
        return (<Typography>No Circuit ID provided.</Typography>);
    }

    const circuitNameMap: Record<string, string> = { [t('mainList.circuits')]: '/circuits' };
    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={circuitNameMap} current={circuit?.name ?? ''} />
                    </Grid>
                    <Grid item xs={12}>
                        {circuit ? <CircuitCard {...props} key={circuit.name} circuit={circuit} /> : <Typography>Loading circuit details...</Typography>}
                    </Grid>
                    <Grid item xs={12}>
                        <Paper className={classes.paper}>
                            <TrackdaysCalendarControl eventsFeed={`${config.ontimeApi}/api/circuits/${circuitId}/calendarfeed`} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h5" gutterBottom>{t("circuit.explore")}</Typography>
                        <Button
                            variant="outlined"
                            startIcon={<EventIcon />}
                            className={classes.navButton}
                            onClick={() => navigate(`/circuits/${circuitId}/events`)}
                        >
                            {t("circuit.events")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<TwoWheelerIcon />}
                            className={classes.navButton}
                            onClick={() => navigate(`/circuits/${circuitId}/hall-of-fame/bikes`)}
                        >
                            {t("circuit.bikesHallOfFame")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DriveEtaIcon />}
                            className={classes.navButton}
                            onClick={() => navigate(`/circuits/${circuitId}/hall-of-fame/cars`)}
                        >
                            {t("circuit.carsHallOfFame")}
                        </Button>
                    </Grid>
                </Grid>
            </Container>
        </main >
    );
}


export default CircuitDetail;